import React, {useCallback, useMemo, useState} from 'react';
import block from 'bem-cn-lite';
import {compose} from 'recompose';
import {BlockHeader, Row, Col, GridColumnSize} from '@yandex-data-ui/page-constructor';

import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import withServicesData, {WithServicesDataProps} from 'hoc/withServicesData';

import {ServicesBlockProps} from 'units/constructor-local/models';
import Search from 'units/marketplace/components/Search/Search';
import NavigationItems from 'units/navigation/components/NavigationItems/NavigationItems';
import {default as DesktopMenu, MenuSubtype, MenuType} from 'components/StickyMenu/StickyMenu';
import {default as MobileMenu} from 'units/constructor-local/components/Menu/Menu';
import {getMenuItems} from '../../utils';
import {FEATURED} from 'units/services/constants';

import './Layout.scss';

const b = block('Services');

type ServicesProps = ServicesBlockProps & WithServicesDataProps & WithTranslationProps;

const Services: React.FC<ServicesProps> = ({
    title,
    description,
    size = 'm',
    servicesData,
    i18nK,
}) => {
    const {categories, dataByCategory} = servicesData;
    const [search, setSearch] = useState('');

    const filteredCategories = useMemo(
        () => (size === 's' ? categories.filter(({slug}) => slug !== FEATURED) : categories),
        [categories, size],
    );
    const menuItems = getMenuItems(filteredCategories);

    const handleSearch = useCallback((value: string) => setSearch(value), []);
    const resetSearch = useCallback(() => {
        if (search) {
            setSearch('');
        }
    }, [search]);

    const header = useMemo(() => {
        if (!title) {
            return null;
        }

        const blockTitle = typeof title === 'string' ? {text: title} : title;

        return (
            <BlockHeader
                title={{...blockTitle, textSize: size === 's' ? 'm' : 'l'}}
                description={description}
                className={b('title')}
            />
        );
    }, [description, size, title]);

    return (
        <div className={b({size})}>
            {header}
            <Search
                initialValue=""
                onSubmit={handleSearch}
                className={b('search', {size})}
                placeholder={i18nK('search-placeholder')}
                value={search}
                size={size}
                autoFocus={true}
            />
            <Row className={b('list')}>
                <MobileMenu
                    items={menuItems}
                    className={b('mobile-menu')}
                    onSectionClick={resetSearch}
                >
                    <h5>{i18nK('services-menu-title')}</h5>
                </MobileMenu>
                <Col
                    sizes={{
                        [GridColumnSize.Lg]: 9,
                        [GridColumnSize.Md]: 8,
                        [GridColumnSize.All]: 12,
                    }}
                >
                    <NavigationItems
                        dataByCategory={dataByCategory}
                        categories={filteredCategories}
                        size={size}
                        search={search}
                    />
                </Col>
                <Col
                    sizes={{
                        [GridColumnSize.Lg]: 3,
                        [GridColumnSize.All]: 4,
                    }}
                    visible={GridColumnSize.Md}
                >
                    <DesktopMenu
                        key={String(!search)}
                        className={b('desktop-menu')}
                        type={MenuType.Vertical}
                        subtype={MenuSubtype.Services}
                        title={i18nK('services-menu-title')}
                        items={menuItems}
                        onSectionClick={resetSearch}
                    />
                </Col>
            </Row>
        </div>
    );
};

export default compose<ServicesProps, ServicesBlockProps>(
    withServicesData,
    withTranslation('services'),
)(Services);
